// =============================================================================
// helpCatalog.ts — the catalogue of help_content keys, labels + tab grouping
// =============================================================================
// The `help_content` table stores only { key, text }; the friendly LABEL and the
// tab grouping live here in the frontend (there is no label column). Extracted
// from HelpContentAdmin so BOTH the admin editor and the Resources "link to a
// help item" picker read one source — the labels can never drift between them.
//
// Keep in step with the (?) HelpButton call sites: a new help key means a new row
// here (label + which tab it belongs on).
// =============================================================================

export interface HelpCatalogEntry {
  key: string;
  label: string;
  desc: string;
}

export type HelpTabId = "general" | "responder" | "shelter";

export const HELP_TABS: { id: HelpTabId; label: string }[] = [
  { id: "general", label: "General" },
  { id: "responder", label: "Responder" },
  { id: "shelter", label: "DFV Service" },
];

export const HELP_CATALOG: HelpCatalogEntry[] = [
  {
    key: "welcome_overview",
    label: "Welcome Guide",
    desc: "Shown to shelter users on first login",
  },
  { key: "general_info", label: "General Info", desc: "Help for the General Information section" },
  {
    key: "general_contact_phone",
    label: "Contact Phone (New Request)",
    desc: "'?' beside Contact Phone on the New Request General tab",
  },
  {
    key: "general_delivery",
    label: "Delivery Address (New Request)",
    desc: "'?' beside Delivery Address on the New Request General tab",
  },
  {
    key: "request_worksheet",
    label: "Preparation Checklist",
    desc: "'Preparation checklist' bubble on the New Request wizard header",
  },
  {
    key: "invoices_payment",
    label: "Invoice Requirements",
    desc: "'Invoice requirements' bubble on the New Request wizard header",
  },
  { key: "key_contacts", label: "Key Contacts", desc: "Phone, email, postal address" },
  { key: "request_status", label: "Status Guide", desc: "What each request status means" },
  {
    key: "what_happens_next",
    label: "After Submitting",
    desc: "What happens after submitting a request",
  },
  { key: "approvals_overview", label: "Approvals Overview", desc: "Approval Decisions help text" },
  { key: "uploads", label: "File Uploads", desc: "Help for file uploads section" },
  {
    key: "files_invoices",
    label: "Files & Invoices - Invoices",
    desc: "'?' beside the Invoices column in the Files & Invoices modal",
  },
  {
    key: "cold_climate",
    label: "Cold Climate (Uniforms)",
    desc: "'Cold Climate' bubble in the Uniforms section",
  },
  {
    key: "fees_overview",
    label: "School Costs & Stationery (overview)",
    desc: "(?) bubble on the School Costs & Stationery section",
  },
  { key: "uniforms", label: "Uniforms (overview)", desc: "(?) bubble on the Uniforms section" },
  { key: "uniforms_shoes", label: "Shoes", desc: "(?) bubble on the Shoes section" },
  { key: "school_locker", label: "School Locker", desc: "(?) bubble on the School Locker section" },
  {
    key: "school_locker_sku",
    label: "School Locker SKU",
    desc: "(?) bubble on the School Locker SKU field",
  },
  {
    key: "reimbursement",
    label: "Reimbursement",
    desc: "(?) bubble on the reimbursement option (Files step)",
  },
  {
    key: "comments_responding",
    label: "Comments (responding)",
    desc: "(?) bubble on the DFV Service comments",
  },
  { key: "laptop", label: "Device/Laptop", desc: "Device section help" },
  { key: "laptop_eligibility", label: "Device Eligibility", desc: "Device eligibility criteria" },
  {
    key: "device_specs",
    label: "Device Specs (DFV Service)",
    desc: "'Device Specs' button — shelter New Request device form + submitted-request device view",
  },
  {
    key: "device_specs_responder",
    label: "Device Specs (Responder)",
    desc: "'Device Specs' button on the responder request-detail device section",
  },
  {
    key: "responder_edit_fees",
    label: "Responder-Edit School Costs & Stationery",
    desc: "Responder (?) bubble on the Edit School Costs & Stationery button",
  },
  {
    key: "responder_edit_uniforms",
    label: "Responder-Edit Uniforms",
    desc: "Responder (?) bubble on the Edit Uniforms button",
  },
  {
    key: "responder_edit_shoes",
    label: "Responder-Edit Shoes",
    desc: "Responder (?) bubble on the Edit Shoes button",
  },
  {
    key: "responder_edit_device",
    label: "Responder-Edit Device",
    desc: "Responder (?) bubble on the Edit Device button",
  },
  {
    key: "responder_verify_address",
    label: "Responder - Verify Delivery Address",
    desc: "Confirmation shown before a responder verifies a shelter-entered delivery address",
  },
  {
    key: "approvals_confirm_send",
    label: "Approvals - Confirm & Send",
    desc: "Responder (?) bubble on the Confirm & Send step (Approval Decisions)",
  },
  {
    key: "task_actions",
    label: "Responder Task Actions",
    desc: "Responder/Picker (?) bubble on the To-Do task actions",
  },
  {
    key: "scc_stationery",
    label: "SC&C - Stationery",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Stationery",
  },
  {
    key: "scc_textbooks",
    label: "SC&C - Textbooks",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Textbooks",
  },
  {
    key: "scc_stationery_textbooks",
    label: "SC&C - Stationery & Textbooks",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Stationery & Textbooks",
  },
  {
    key: "scc_camp_fees",
    label: "SC&C - Camp Fees",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Camp Fees",
  },
  {
    key: "scc_resource_fees",
    label: "SC&C - Resource Fees",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Resource Fees",
  },
  {
    key: "scc_device_hire",
    label: "SC&C - Device Hire",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Device Hire",
  },
  {
    key: "scc_school_activities",
    label: "SC&C - School Activities",
    desc: "Per-fee (?) bubble — School Costs & Stationery: School Activities",
  },
  {
    key: "scc_non_school_activities",
    label: "SC&C - Non-school Activities",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Non-school Activities",
  },
  {
    key: "scc_swim_gear",
    label: "SC&C - Swim Gear",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Swim Gear",
  },
  {
    key: "scc_other",
    label: "SC&C - Other (specify)",
    desc: "Per-fee (?) bubble — School Costs & Stationery: Other (specify)",
  },
  {
    key: "shelter_scc_stationery",
    label: "DFV Service Fee - Stationery",
    desc: "DFV Service New Request (?) bubble: Stationery",
  },
  {
    key: "shelter_scc_textbooks",
    label: "DFV Service Fee - Textbooks",
    desc: "DFV Service New Request (?) bubble: Textbooks",
  },
  {
    key: "shelter_scc_stationery_textbooks",
    label: "DFV Service Fee - Stationery & Textbooks",
    desc: "DFV Service New Request (?) bubble: Stationery & Textbooks",
  },
  {
    key: "shelter_scc_camp_fees",
    label: "DFV Service Fee - Camp Fees",
    desc: "DFV Service New Request (?) bubble: Camp Fees",
  },
  {
    key: "shelter_scc_resource_fees",
    label: "DFV Service Fee - Resource Fees",
    desc: "DFV Service New Request (?) bubble: Resource Fees",
  },
  {
    key: "shelter_scc_device_hire",
    label: "DFV Service Fee - Device Hire",
    desc: "DFV Service New Request (?) bubble: Device Hire",
  },
  {
    key: "shelter_scc_school_activities",
    label: "DFV Service Fee - School Activities",
    desc: "DFV Service New Request (?) bubble: School Activities",
  },
  {
    key: "shelter_scc_non_school_activities",
    label: "DFV Service Fee - Non-school Activities",
    desc: "DFV Service New Request (?) bubble: Non-school Activities",
  },
  {
    key: "shelter_scc_swim_gear",
    label: "DFV Service Fee - Swim Gear",
    desc: "DFV Service New Request (?) bubble: Swim Gear",
  },
  {
    key: "shelter_scc_other",
    label: "DFV Service Fee - Other (specify)",
    desc: "DFV Service New Request (?) bubble: Other (specify)",
  },
];

// Responder-tab keys (the responder-edit bubbles + the per-fee scc_* bubbles).
const RESPONDER_KEYS = new Set<string>([
  "responder_edit_fees",
  "responder_edit_uniforms",
  "responder_edit_shoes",
  "responder_edit_device",
  "device_specs_responder",
  "responder_verify_address",
  "approvals_confirm_send",
  "task_actions",
  "scc_stationery",
  "scc_textbooks",
  "scc_stationery_textbooks",
  "scc_camp_fees",
  "scc_resource_fees",
  "scc_device_hire",
  "scc_school_activities",
  "scc_non_school_activities",
  "scc_swim_gear",
  "scc_other",
]);

// Shelter-facing (?) bubbles that aren't shelter_scc_* but belong on the DFV
// Service tab (the New Request wizard header + Uniforms Cold Climate help).
const SHELTER_EXTRA_KEYS = new Set<string>([
  "request_worksheet",
  "invoices_payment",
  "cold_climate",
  "fees_overview",
  "uniforms",
  "uniforms_shoes",
  "school_locker",
  "school_locker_sku",
  "reimbursement",
  "comments_responding",
  "device_specs",
]);

// Which tab a help key groups under. Derived from the key, not a per-row field.
export function helpTabOf(key: string): HelpTabId {
  if (key.startsWith("shelter_scc_") || SHELTER_EXTRA_KEYS.has(key)) return "shelter";
  if (RESPONDER_KEYS.has(key)) return "responder";
  return "general";
}

// The friendly label for a help key, or the key itself if it isn't catalogued.
export function helpLabel(key: string): string {
  return HELP_CATALOG.find((h) => h.key === key)?.label || key;
}
